import assetService from 'player/services/api/asset';
import utils from 'utils/utils';
import { Model } from 'utils/model';
import { cloneDeep } from 'utils/clone-deep';
import { CategoryModel } from 'player/model/category';
import type {
    Asset,
    AssetAdditional,
    AssetChapter,
    Nullable,
} from '@schibsted-svp/svp-api-types';

export type ParsedChapter = AssetChapter & {
    time: string;
    index: number;
};

export type ParsedAsset = Omit<Asset, 'category'> &
    Omit<
        AssetAdditional,
        'chapters' | 'tags' | 'playlist' | 'subtitles' | 'featuredChapters'
    > & {
        vendor: string;
        ageLimit: Nullable<number>;
        category: CategoryModel;
        chapters: ParsedChapter[];
        playlist: Asset[];
        captions: jwplayer.Caption[];
        tags: { id: string; tag: string }[];
    };

function unifyTimestamp(time: number) {
    if (!time) {
        return null;
    }

    if (time.toString().length === 10) {
        return time * 1000;
    }

    return time;
}

export class AssetModel extends Model<ParsedAsset> {
    rawAsset: Asset = undefined!;

    constructor(options?: Partial<Asset & { vendor: string }>) {
        super({ id: 0 });

        this.initialize(options);
    }

    initialize(data?: Partial<Asset & { vendor: string }>) {
        utils.extend(this.attributes, this.parse(data as ParsedAsset & Asset));
    }

    getRawAsset() {
        return this.rawAsset;
    }

    parse(
        data: Partial<
            Asset &
                ParsedAsset & {
                    vendor?: string;
                    _embedded: {
                        playlist: Asset[];
                        tags: { id: string; tag: string }[];
                    };
                }
        > = {},
    ) {
        this.rawAsset = cloneDeep(data || {}) as Asset;

        const result = cloneDeep(data || {}) as ParsedAsset;

        // rewrite additional fields
        if (data.additional) {
            Object.entries(data.additional || {}).forEach(([key, value]) => {
                // featured chapters are fetched on the lists
                // merge them
                if (key === 'featuredChapters') {
                    key = 'chapters';
                }

                if (key === 'subtitles') {
                    key = 'captions';
                }

                if (typeof result[key as keyof ParsedAsset] === 'undefined') {
                    // @ts-expect-error too complex to care
                    result[key] = value;
                }
            });

            if (data.additional.settings?.ageLimit !== undefined) {
                result.ageLimit = data.additional.settings.ageLimit;
            }
        }

        // multiply timestamps by 1000 to use ms instead of s
        if (result.flightTimes) {
            result.flightTimes = {
                start: result.flightTimes.start
                    ? unifyTimestamp(result.flightTimes.start)
                    : null,
                end: result.flightTimes.end
                    ? unifyTimestamp(result.flightTimes.end)
                    : null,
            };
        }

        if (data.category && !(result.category instanceof CategoryModel)) {
            result.category = new CategoryModel(result.category);
        }

        if (result.chapters) {
            result.chapters = result.chapters.map((chapter, index) => ({
                ...chapter,
                time: utils.string.getTimeFormatted(chapter.timeline),
                index: index + 1,
            }));
        }

        // next asset is optional, fill with basic object to avoid undefined errors
        if (!result.nextAsset) {
            result.nextAsset = {} as ParsedAsset['nextAsset'];
        }

        result.tags = result.tags || [];

        if (data._embedded && data._embedded.tags) {
            result.tags = data._embedded.tags;
        }

        result.playlist = [];

        if (data._embedded && data._embedded.playlist) {
            result.playlist = data._embedded.playlist;
        }

        return result;
    }

    async fetch() {
        const asset = await assetService.getById({
            vendor: this.getVendor(),
            assetId: this.getId(),
        });
        Object.assign(
            this.attributes,
            this.parse(asset as ParsedAsset & Asset),
        );
    }

    getVendor(): string {
        return this.get('vendor');
    }

    getId(): number {
        return this.get('id');
    }

    getStreamType(): 'live' | 'wasLive' | 'vod' {
        return this.get('streamType');
    }

    getNextAssetId(): number | undefined {
        return this.get('nextAsset')?.id;
    }

    destroy() {
        // in case of any cleanup
    }

    getAgeLimit(): number {
        const ageLimit = this.get('ageLimit');
        return utils.isNumber(ageLimit) ? ageLimit! : -1;
    }

    isSponsored(): boolean {
        const metadata = this.get('metadata');

        let isSponsored = false;

        /**
         * SVP API metadata values are always strings. There can be different structures serialized to string though.
         */
        if (metadata && metadata.sponsored) {
            try {
                isSponsored =
                    JSON.parse(metadata.sponsored.toLowerCase()) === true;
            } catch {
                // All the cases that fail to be json decoded are treated as false
            }
        }

        return isSponsored;
    }

    /**
     * Get chapter by its index (numbered from 1)
     */
    getChapter(chapterIndex: number): ParsedChapter | undefined {
        return this.getChapters()[chapterIndex - 1];
    }

    /**
     * Get chapters for single stream
     */
    getChapters(): ParsedChapter[] {
        return this.get('chapters') || [];
    }

    getThumbnail(): string {
        return this.get('images').main;
    }

    getTitle(): string {
        return this.get('title');
    }

    getCategory(): CategoryModel {
        return this.get('category');
    }

    isAudio(): boolean {
        return this.get('assetType') === 'audio';
    }
}
